import { useIntl } from 'react-intl';
import BusinessIcon from '@mui/icons-material/Business';
import { PRODUCT_COMPANY_STATUS_DISPLAY_ORDER } from 'enums';
import { Box, Chip, IconButton, Tooltip, useTheme } from '@mui/material';
import { getProjectCompanyCodeStatusCounts, normalizeProjectCompanyCodes } from 'utils';
import { getProductCompanyStatusLabel, getProductCompanyStatusVisual } from 'components/product-company-status-chip';

type ProductCompanyCodesCellProps = {
  value: unknown;
  editable: boolean;
  onClick: () => void;
};

const ProductCompanyCodesCell = ({ value, editable, onClick }: ProductCompanyCodesCellProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const companyCodes = normalizeProjectCompanyCodes(value);
  const statusCounts = getProjectCompanyCodeStatusCounts(companyCodes);
  const actionLabel = intl.formatMessage({
    id: editable ? 'project.table.manageCompanyCodes' : 'project.table.showCompanyCodes',
    defaultMessage: editable ? 'Şirket Kodlarını Yönet' : 'Şirket Kodlarını Göster',
  });
  const statusSummary = PRODUCT_COMPANY_STATUS_DISPLAY_ORDER.filter((status) => statusCounts[status] > 0)
    .map((status) => `${statusCounts[status]} ${getProductCompanyStatusLabel(status, intl)}`)
    .join(' · ');

  if (companyCodes.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
        <Tooltip title={actionLabel}>
          <Chip
            icon={<BusinessIcon fontSize="small" />}
            label={intl.formatMessage({
              id: editable ? 'project.table.selectCompanyCodes' : 'project.table.noCompanyCodes',
              defaultMessage: editable ? 'Şirket Seç' : 'Tanımlı şirket yok',
            })}
            size="small"
            variant="outlined"
            color={editable ? 'primary' : 'default'}
            aria-label={actionLabel}
            onClick={onClick}
            sx={{
              height: 24,
              fontSize: '0.72rem',
              fontWeight: 600,
              cursor: 'pointer',
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </Tooltip>
      </Box>
    );
  }

  const displayedCodes = companyCodes.slice(0, 2);
  const remainingCodeCount = companyCodes.length - displayedCodes.length;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      gap={0.5}
      width="100%"
      height="100%"
      overflow="hidden">
      <Tooltip title={`${actionLabel} · ${statusSummary}`}>
        <IconButton
          size="small"
          aria-label={`${actionLabel}: ${statusSummary}`}
          sx={{ border: 'none', p: 0.5, flexShrink: 0 }}
          onClick={onClick}>
          <BusinessIcon fontSize="small" color="primary" />
        </IconButton>
      </Tooltip>

      <Box display="flex" gap={0.5} alignItems="center" minWidth={0} overflow="hidden">
        {displayedCodes.map(({ companyCode, status }) => {
          const statusLabel = getProductCompanyStatusLabel(status, intl);
          const visual = getProductCompanyStatusVisual(status, theme);

          return (
            <Tooltip key={companyCode} title={`${companyCode}: ${statusLabel}`}>
              <Chip
                icon={visual.icon}
                label={`${companyCode} · ${statusLabel}`}
                size="small"
                variant="outlined"
                aria-label={`${companyCode}: ${statusLabel}`}
                onClick={onClick}
                sx={{
                  height: 22,
                  minWidth: 0,
                  maxWidth: 135,
                  flexShrink: 1,
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: visual.color,
                  borderColor: visual.borderColor,
                  bgcolor: visual.backgroundColor,
                  '& .MuiChip-icon': { color: 'inherit', ml: 0.5 },
                  '& .MuiChip-label': { px: 0.8, overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
              />
            </Tooltip>
          );
        })}

        {remainingCodeCount > 0 && (
          <Tooltip title={statusSummary}>
            <Chip
              label={`+${remainingCodeCount}`}
              size="small"
              color="primary"
              aria-label={`${intl.formatMessage(
                { id: 'report.companyCodeCount', defaultMessage: '{count} şirket kodu' },
                { count: remainingCodeCount }
              )}. ${statusSummary}`}
              onClick={onClick}
              sx={{
                height: 22,
                flexShrink: 0,
                fontSize: '0.72rem',
                fontWeight: 700,
                cursor: 'pointer',
                '& .MuiChip-label': { px: 0.8 },
              }}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default ProductCompanyCodesCell;
